import React from 'react';

interface MinterUsageCardProps {
  metrics: {
    dailyLimit: string;
    usedToday: string;
    remainingToday: string;
    percentageUsed: number;
    lastResetAt: string;
  } | null;
}

const MinterUsageCard: React.FC<MinterUsageCardProps> = ({ metrics }) => {
  if (!metrics) {
    return (
      <div className="card p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
        <div className="h-8 bg-gray-200 rounded w-1/2"></div>
      </div>
    );
  }

  const percentageUsed = metrics.percentageUsed;
  const status: 'healthy' | 'warning' | 'critical' =
    percentageUsed >= 90 ? 'critical' : percentageUsed >= 80 ? 'warning' : 'healthy';

  const statusColors = {
    healthy: 'border-green-500 bg-green-50',
    warning: 'border-yellow-500 bg-yellow-50',
    critical: 'border-red-500 bg-red-50',
  };

  const progressColors = {
    healthy: 'bg-green-500',
    warning: 'bg-yellow-500',
    critical: 'bg-red-500',
  };

  const badgeColors = {
    healthy: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    critical: 'bg-red-100 text-red-800',
  };

  return (
    <div className={`card border-l-4 ${statusColors[status]} p-6`}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-500 mb-1">Daily Minting Limit</h3>
          <p className="text-3xl font-bold text-gray-900">
            {metrics.usedToday} / {metrics.dailyLimit} HEP
          </p>
          <p className="text-sm text-gray-600 mt-1">
            {metrics.remainingToday} HEP remaining
          </p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badgeColors[status]}`}>
          {status.toUpperCase()}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
          <div
            className={`h-full ${progressColors[status]} transition-all duration-300 ease-in-out`}
            style={{ width: `${Math.min(percentageUsed, 100)}%` }}
          />
        </div>
      </div>

      <div className="flex justify-between items-center text-xs">
        <span className="text-gray-600 font-medium">{percentageUsed.toFixed(1)}% used</span>
        <span className="text-gray-500">
          Resets at {new Date(metrics.lastResetAt).toLocaleTimeString()}
        </span>
      </div>

      {/* Status Messages */}
      {status === 'critical' && (
        <div className="mt-3 p-2 bg-red-100 border border-red-200 rounded text-xs text-red-800">
          <strong>Critical:</strong> Approaching daily limit. Manual approvals may be required.
        </div>
      )}
      {status === 'warning' && (
        <div className="mt-3 p-2 bg-yellow-100 border border-yellow-200 rounded text-xs text-yellow-800">
          <strong>Warning:</strong> Daily limit at {percentageUsed.toFixed(0)}%. Monitor closely.
        </div>
      )}
    </div>
  );
};

export default MinterUsageCard;
